import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getProduct, removeProduct, updateProduct } from "slice/productSlice";
import AdminTable from "common/AdminTable";
import TruncatedMessage from "common/TruncatedMessage";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import useCities from "hooks/useCities";
import "./ProductList.css";

const ProductList = () => {
    const dispatch = useDispatch();
    const cartData = useSelector((state) => state.product);

    const [showModal, setShowModal] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [selectedCity, setSelectedCity] = useState("");
    const { cities } = useCities();

    useEffect(() => {
        dispatch(getProduct());
    }, [dispatch]);

    const filteredData = useMemo(() => {
        if (!selectedCity) return cartData?.data || [];
        return (cartData?.data || []).filter(
            (product) => product.city === selectedCity
        );
    }, [cartData?.data, selectedCity]);

    const handleEditClick = (product) => {
        setCurrentProduct({
            ...product,
            is_active: Boolean(product.is_active),
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentProduct(null);
        setImageFile(null);
    };

    const handleSave = async () => {
        try {
            const formData = new FormData();
            formData.append("id", currentProduct.id);
            formData.append("name", currentProduct.name);
            formData.append("long_description", currentProduct.long_description);
            formData.append("is_active", currentProduct.is_active);

            if (imageFile) {
                formData.append("image", imageFile);
            }

            await dispatch(
                updateProduct({
                    id: currentProduct.id,
                    data: formData,
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
            );

            toast.success("Service Updated Successfully!");
            handleCloseModal();
        } catch (error) {
            toast.error("Update Failed!");
        }
    };

    const handleRemove = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(removeProduct(id));
                Swal.fire("Deleted!", "The product has been removed.", "success");
            }
        });
    };

    const handleImageChange = (event) => {
        setImageFile(event.target.files[0]);
    };

    const tableHeaders = [
        {
            Header: "ID",
            accessor: "id",
        },
        {
            Header: "Image",
            accessor: "image",
            Cell: ({ row }) => (
                <img
                    src={`${process.env.REACT_APP_BACKEND}asserts/product/${row.original.images}`}
                    alt={row.original.name}
                    width={100}
                />
            ),
        },
        {
            Header: "Name",
            accessor: "name",
        },
        {
            Header: "City",
            accessor: "city",
        },
        {
            Header: "Created Date",
            accessor: "createdAt",
        },
        {
            Header: "Description",
            accessor: "long_description",
            Cell: ({ row }) => (
                <TruncatedMessage
                    text={row.original.long_description}
                    maxLength={30}
                />
            ),
        },
        {
            Header: "Active",
            accessor: "is_active",
            Cell: ({ row }) => (
                <span
                    className={
                        row.original.is_active
                            ? "badge badge-success"
                            : "badge badge-danger"
                    }
                >
                    {row.original.is_active ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            Header: "Actions",
            accessor: "actions",
            Cell: ({ row }) => (
                <>
                    <button
                        className="btn removebtn mx-1 my-1"
                        onClick={() => handleRemove(row.original.id)}
                    >
                        Remove
                    </button>
                    <button
                        className="btn editbtn mx-1 my-1"
                        onClick={() => handleEditClick(row.original)}
                    >
                        Edit
                    </button>
                </>
            ),
        },
    ];

    return (
        <>
            <div className="container-fluid mt-5">
                <div className="mainheadig mx-4">
                    <h4 className="text-white font-weight-bold">Service List</h4>
                </div>

                <div className="mx-4 mb-3" style={{ maxWidth: 250 }}>
                    <label htmlFor="cityFilter" className="lables">Filter by City</label>
                    <select
                        id="cityFilter"
                        className="form-control"
                        value={selectedCity}
                        onChange={(e) => setSelectedCity(e.target.value)}
                    >
                        <option value="">All Cities</option>
                        {cities.map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>

                {filteredData.length > 0 && (
                    <AdminTable
                        tableHeaders={tableHeaders}
                        tableData={filteredData}
                    />
                )}
            </div>

            {showModal && currentProduct && (
                <div
                    className="modal fade show"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">

                            <div className="modal-header">
                                <h5 className="modal-title">Edit Service</h5>
                                <button
                                    type="button"
                                    className="close"
                                    onClick={handleCloseModal}
                                >
                                    <span>&times;</span>
                                </button>
                            </div>

                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="productStatus">Status</label>
                                    <select
                                        id="productStatus"
                                        className="form-control"
                                        value={currentProduct.is_active}
                                        onChange={(e) =>
                                            setCurrentProduct({
                                                ...currentProduct,
                                                is_active: e.target.value === "true",
                                            })
                                        }
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="productName">Name</label>
                                    <input
                                        type="text"
                                        id="productName"
                                        className="form-control"
                                        value={currentProduct.name}
                                        onChange={(e) =>
                                            setCurrentProduct({
                                                ...currentProduct,
                                                name: e.target.value,
                                            })
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="productDescription">
                                        Description
                                    </label>
                                    <textarea
                                        id="productDescription"
                                        className="form-control"
                                        value={currentProduct.long_description}
                                        onChange={(e) =>
                                            setCurrentProduct({
                                                ...currentProduct,
                                                long_description: e.target.value,
                                            })
                                        }
                                    />
                                </div>

                                <div className="form-group">
                                    <div className="image-section-title">
                                        Update Product Image
                                    </div>

                                    <div htmlFor="image2" className="image-upload-label image-upload-box">

                                        <input
                                            type="file"
                                            id="image2"
                                            name="image"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: "none" }}
                                        />

                                        <div className="upload-text text-center">
                                            <h5>Click to upload image</h5>
                                        </div>

                                    </div>

                                    {imageFile && (
                                        <div className="mt-3 text-center">
                                            <img
                                                src={URL.createObjectURL(imageFile)}
                                                alt="preview"
                                                width={120}
                                            />
                                            <p>{imageFile.name}</p>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => setImageFile(null)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </div>

                            </div>

                            <div className="modal-footer">
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleCloseModal}
                                >
                                    Close
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                >
                                    Save Changes
                                </button>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ProductList;
